import { eventById, EVENTS, formatMark, type UserStats } from '@decathlon/shared';
import { clear, EVENT_EMOJI, h, wrapTables } from '../dom.js';
import { barChart, lineChart } from './charts.js';

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
const tile = (v: string, l: string) =>
  h('div', { class: 'stat' }, h('div', { class: 'v' }, v), h('div', { class: 'l' }, l));
const arrow = (e: { count: number; recentPoints: number; earlierPoints: number }) =>
  e.count < 6
    ? ''
    : e.recentPoints > e.earlierPoints
      ? ' ↑'
      : e.recentPoints < e.earlierPoints
        ? ' ↓'
        : ' →';

export function renderStats(
  root: HTMLElement,
  stats: UserStats,
  local: boolean,
  onBack: () => void,
): void {
  clear(root);
  const s = stats.summary;

  const pointsPts = stats.results.map((r, i) => ({
    x: i,
    y: r.points,
    label: `${fmtDate(r.playedAt)}: ${eventById(r.event).name} ${formatMark(eventById(r.event), r.mark)} = ${r.points} pts`,
  }));
  const bestBars = stats.events.map((e) => ({
    label: eventById(e.event).short,
    value: e.bestPoints,
    detail: `${eventById(e.event).name}: best ${formatMark(eventById(e.event), e.best)} in ${e.count} attempt${e.count === 1 ? '' : 's'}`,
  }));

  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel wide stack' },
        h(
          'div',
          { class: 'row', style: 'justify-content:space-between' },
          h('h1', {}, `📈 ${stats.name}'s progress`),
          h('button', { class: 'btn small ghost', onclick: onBack }, '← Back'),
        ),
        local
          ? h(
              'p',
              { class: 'muted small' },
              'Stored in this browser. Create an account to keep your progress across devices.',
            )
          : null,
        h(
          'div',
          { class: 'row' },
          tile(String(s.results), 'Results'),
          tile(`${s.eventsTried}/${EVENTS.length}`, 'Events tried'),
          tile(String(s.bestTotal), 'Dream total'),
          tile(String(s.currentTotal), 'Latest total'),
        ),
        h(
          'p',
          { class: 'muted small' },
          'Personal progress includes self-reported practice. Public best-event records use server-verified online results.',
        ),
        h(
          'p',
          { class: 'muted small' },
          'Dream total adds your best points in every event you have tried. Daley Thompson scored 8847 over ten events; 700 points per event is a strong club athlete.',
        ),
        h(
          'table',
          {},
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', {}, 'Event'),
              h('th', { class: 'num' }, 'Attempts'),
              h('th', { class: 'num' }, 'Personal best'),
              h('th', { class: 'num' }, 'Best points'),
              h('th', { class: 'num' }, 'Average'),
            ),
          ),
          h(
            'tbody',
            {},
            EVENTS.map((ev) => {
              const e = stats.events.find((x) => x.event === ev.id);
              return h(
                'tr',
                { class: e ? '' : 'muted' },
                h('td', {}, `${EVENT_EMOJI[ev.id] ?? ''} ${ev.name}`),
                h('td', { class: 'num' }, e ? String(e.count) : '—'),
                h('td', { class: 'num' }, e ? formatMark(ev, e.best) : '—'),
                h('td', { class: 'num' }, e ? String(e.bestPoints) : '—'),
                h('td', { class: 'num' }, e ? `${e.avgPoints}${arrow(e)}` : '—'),
              );
            }),
          ),
        ),
        h(
          'div',
          { class: 'charts' },
          lineChart(pointsPts, {
            colour: '#1e88e5',
            title: 'IAAF points, result by result',
            format: (v) => String(Math.round(v)),
            yMin: 0,
            empty: 'Complete an event to start your history.',
          }),
          lineChart(
            stats.results
              .filter((r) => r.cadence)
              .map((r, i) => ({
                x: i,
                y: r.cadence!.avgCadence,
                label: `${eventById(r.event).name}: ${r.cadence!.avgCadence.toFixed(1)}/s${r.cadence!.suspicious ? ' · unusually regular' : ''}`,
              })),
            {
              colour: '#f57c00',
              title: 'Cadence history',
              format: (v) => `${v.toFixed(1)}/s`,
              yMin: 0,
              empty: 'New results include cadence coaching.',
            },
          ),
          barChart(bestBars, {
            colour: '#43a047',
            title: 'Best points per event',
            format: (v) => String(Math.round(v)),
            max: 1100,
          }),
        ),
      ),
    ),
  );
  wrapTables(root);
}
